import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getModulePath, LEAGUE_MODULE_NAMES } from '@leagueplatform/core';
import { HEADER_NAV_BAR_DEFAULT_HEIGHT } from '@leagueplatform/solutions-foundation';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';

const PAGE_HEADER_SELECTOR =
  '.masonry-page-header-background, .cc-page-header-background';

function isTransparentHeaderPage(pathname: string): boolean {
  return [
    APP_MANAGED_ROUTES.HOME,
    APP_MANAGED_ROUTES.CAREGIVER_HOME,
    getModulePath(LEAGUE_MODULE_NAMES.careDiscovery),
    getModulePath(LEAGUE_MODULE_NAMES.healthJourney),
  ].some((path) => pathname === path);
}

export function useTransparentHeader(): boolean {
  const { pathname } = useLocation();
  const [isTransparent, setIsTransparent] = useState(false);
  const isAllowedPage = isTransparentHeaderPage(pathname);

  useEffect(() => {
    if (isAllowedPage) {
      document.body.classList.add('transparent-header-page');
    } else {
      document.body.classList.remove('transparent-header-page');
    }
    return () => {
      document.body.classList.remove('transparent-header-page');
    };
  }, [isAllowedPage]);

  useEffect(() => {
    if (!isAllowedPage) {
      setIsTransparent(false);
      return undefined;
    }

    let hasPageHeader = false;

    function updateScroll() {
      if (!hasPageHeader) {
        setIsTransparent(false);
        return;
      }
      setIsTransparent(window.scrollY < HEADER_NAV_BAR_DEFAULT_HEIGHT);
    }

    function checkPageHeader() {
      hasPageHeader = Boolean(document.querySelector(PAGE_HEADER_SELECTOR));
      updateScroll();
    }

    // MutationObserver detects when a page header is added/removed from the DOM
    // (masonry content loads async, after the header mounts)
    const mutationObserver = new MutationObserver(checkPageHeader);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('scroll', updateScroll, { passive: true });
    checkPageHeader();

    return () => {
      mutationObserver.disconnect();
      window.removeEventListener('scroll', updateScroll);
    };
  }, [isAllowedPage]);

  return isTransparent;
}
